import type { ComboBoxOption } from '@interstate/components/ComboBox';
import { kebabCase } from '@makemydeal/dr-common-utils';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { dealLockHooks } from '../../../hooks';
import { convertToNumber } from '../../../utils/formatUtils';
import type { ComboBoxPairs } from '../utils';
import { buildComboOption } from '../utils';
import { utils } from '@makemydeal/dr-offer-redux';
import type { IPaymentTermMessage } from '@makemydeal/dr-platform-shared';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { selectFocusedInput, useSelectOnFocusEnabled } from '@makemydeal/dr-shared-components/select-on-focus';
import type { FocusEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    CUSTOM_MILEAGE_ERROR,
    CUSTOM_MILEAGE_LABEL,
    CUSTOM_MILEAGE_MAX_LENGTH,
    CUSTOM_MILEAGE_TESTID,
    MILES_LABEL
} from '../../../constants';

enum ComboActions {
    NO_ACTION,
    ORIGIN_VALUE,
    SUBMIT_VALUE
}
const MATCH_MILES_INDEX = 2;
const valueMatcher = new RegExp(/^\s*(\d+)(\D+\w*|)$/);
const messageMatcher = new RegExp(
    /^(Exceeded Maximum Yearly Miles of|Ensure this value is less than or equal to)\D*(\d+)\.?$/,
    'i'
);

export const getMatchedValue = (selectedValue: string): string => {
    const matches = selectedValue?.match(valueMatcher);
    return matches?.length ? matches[1] : '';
};

export const getMessageMileage = (messages: IPaymentTermMessage[]): number => {
    let maxValue = CUSTOM_MILEAGE_MAX_LENGTH;
    if (Array.isArray(messages)) {
        messages.forEach((msg) => {
            if (typeof msg?.message !== 'string') {
                return;
            }
            const values = msg.message.match(messageMatcher);
            if (values != null && values[MATCH_MILES_INDEX] != null) {
                maxValue = parseInt(values[MATCH_MILES_INDEX], 10);
                return;
            }
        });
    }
    return maxValue;
};

export const useCustomMileageField = () => {
    const dispatch = useDispatch();

    const mounted = useRef(false);

    const enableLeaseContractField = useSelector(featureToggleSelectors.isEnableLeaseContractFields);
    const isAutoSelectEnabled = useSelectOnFocusEnabled();
    const isPaymentError = useSelector(offerReduxSelectors.getIsError);
    const isCalculatingPayment = useSelector(offerReduxSelectors.isCalculatingPayment);
    const isLocked = dealLockHooks.useIsDealLocked();
    const paymentErrorMessages = useSelector(utils.getPaymentErrorMessages);
    const messagesFromCurrentTerm = useSelector(offerReduxSelectors.getMessagesFromCurrentTerm);

    const dataValue = useSelector(offerReduxSelectors.getInputAnnualMiles);
    const dataOptions = useSelector(offerReduxSelectors.getAnnualMilesOptions);

    const [maxValue, setMaxValue] = useState(CUSTOM_MILEAGE_MAX_LENGTH);
    const [showValue, setShowValue] = useState({ value: dataValue, action: ComboActions.NO_ACTION });
    const [slideOut, setSlideOut] = useState(false);
    const prevValueBeforeBlur = useRef<number | null>(showValue.value);
    const toggleSlideOut = useCallback(() => {
        setSlideOut(!slideOut);
    }, [slideOut]);

    useEffect(() => {
        if (dataValue && dataValue !== showValue.value) {
            setShowValue({ value: dataValue, action: ComboActions.NO_ACTION });
        }
    }, [dataValue]);

    const reducer = useCallback(
        (actionType: ComboActions): void => {
            switch (actionType) {
                case ComboActions.SUBMIT_VALUE:
                    setShowValue({ value: showValue.value, action: ComboActions.NO_ACTION });
                    dispatch(offerReduxActionCreators.selectedAnnualMiles(showValue.value));
                    break;

                case ComboActions.ORIGIN_VALUE:
                    setShowValue({ value: showValue.value, action: ComboActions.NO_ACTION });
                    break;

                default:
                    break;
            }
        },
        [showValue.value, showValue.action]
    );

    const createOptions = useMemo((): ComboBoxOption[] => {
        return (dataOptions as ComboBoxPairs[])?.map((item: ComboBoxPairs) => buildComboOption(item));
    }, [JSON.stringify(dataOptions)]);

    const currentOption = useCallback(
        (value: number): ComboBoxOption => buildComboOption({ id: value, name: `${value} ${MILES_LABEL}` }),
        []
    );

    const checkRange = useCallback(
        (selectedValue: number): boolean => {
            if (selectedValue === 0) {
                return true;
            }
            return Boolean(selectedValue && selectedValue > 0 && selectedValue <= maxValue);
        },
        [maxValue]
    );

    const isError = useMemo(() => {
        if (isCalculatingPayment || showValue.value === 0) {
            return false;
        }
        return !checkRange(showValue.value);
    }, [showValue.value, maxValue, isCalculatingPayment]);

    const comboBoxValue = useMemo(() => {
        return showValue.value != null ? [currentOption(showValue.value)] : undefined;
    }, [showValue.value, showValue.action]);

    useEffect(() => {
        if (!isCalculatingPayment) {
            const errors = isPaymentError ? paymentErrorMessages : messagesFromCurrentTerm;
            const value = getMessageMileage(errors);
            if (value) {
                setMaxValue(value);
            }
        }
    }, [isCalculatingPayment, isPaymentError, JSON.stringify(messagesFromCurrentTerm)]);

    useEffect(() => {
        if (!mounted.current) {
            mounted.current = true;
            return;
        }
        reducer(showValue.action);
    }, [showValue.action]);

    const handleChange = (event: any) => {
        const [selectedMileage] = event.target.value;

        if (selectedMileage == null) {
            return;
        }

        dispatch(interactionAnalyticsActionCreators.milesPerYearDropdown());
        const strValue = getMatchedValue(selectedMileage.value);
        const numValue = convertToNumber(strValue);
        let action = ComboActions.ORIGIN_VALUE;
        if (checkRange(numValue)) {
            if (numValue === showValue.value) {
                return setShowValue({ value: numValue, action });
            } else {
                action = ComboActions.SUBMIT_VALUE;
                dispatch(offerActionCreators.updatePaymentCallPendingState(true));
                validateAndApplyChanges();
                return setShowValue({ value: numValue, action });
            }
        } else {
            return setShowValue({ value: numValue, action: ComboActions.ORIGIN_VALUE });
        }
    };

    const validateAndApplyChanges = () => {
        if (prevValueBeforeBlur.current !== showValue.value) {
            prevValueBeforeBlur.current = showValue.value;
            if (checkRange(showValue.value)) {
                return setShowValue({ value: showValue.value, action: ComboActions.SUBMIT_VALUE });
            }
        }
    };

    const handleKeyDown = (event: any) => {
        if (event.key !== 'Enter') {
            return;
        }
        validateAndApplyChanges();
    };

    const handleComboBoxFocus = (e: FocusEvent<HTMLElement>) => {
        if (isAutoSelectEnabled) selectFocusedInput(e);
    };

    return {
        label: CUSTOM_MILEAGE_LABEL,
        isLocked,
        hasOptions: enableLeaseContractField,
        isSlideOutOpen: slideOut,
        toggleSlideOut,
        onKeyDown: handleKeyDown,
        onComboBoxFocus: handleComboBoxFocus,
        comboBoxProps: {
            id: kebabCase(CUSTOM_MILEAGE_LABEL),
            value: comboBoxValue,
            options: createOptions,
            onChange: handleChange,
            size: 'sm' as const,
            allowNewEntry: true,
            hasError: isError,
            errorMessage: CUSTOM_MILEAGE_ERROR + maxValue,
            displayNoDataMessage: false,
            disabled: isLocked,
            'data-testid': kebabCase(CUSTOM_MILEAGE_TESTID),
            sx: {
                '& .ids-combo-box-multi-select-input': {
                    padding: 0
                }
            }
        }
    };
};
